import React, { useState, useEffect, useRef, useContext } from "react";
import { Link } from "react-router-dom";
import { gsap } from "gsap";
import {
  Search,
  User,
  ShoppingBag,
  X,
  Menu,
  ChevronDown,
  ChevronRight,
  Heart,
} from "lucide-react";
import CartOffCanvas from "./CartOffCanvas";
import { useCart } from "../context/CartContext";
import Auth from "../utils/AuthUtils";
import { CategoryContext } from "../context/CategoryContext";
import { useSiteConfig } from "../context/SiteConfigContext";
import productAPI from "../api/productApi";

const Header = () => {
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { config } = useSiteConfig();

  // ── Search States ──────────────────────────────────────────────────────
  const [searchText, setSearchText] = useState("");
  const [searchProducts, setSearchProducts] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);

  // ── Mobile accordion: which category is expanded ───────────────────────
  const [expandedCat, setExpandedCat] = useState(null);

  // ── API / Context ──────────────────────────────────────────────────────
  const { cartItems } = useCart();
  const isLoggedIn = Auth.getToken();
  const { categories } = useContext(CategoryContext);

  const cartCount = cartItems.reduce(
    (sum, item) => sum + (item.quantity || item.qty || 1),
    0,
  );

  const headerRef = useRef(null);
  const searchOverlayRef = useRef(null);
  const menuOverlayRef = useRef(null);
  const searchTl = useRef(null);
  const menuTl = useRef(null);

  const staticNavLinks = [
    { label: "New Arrivals", path: "/new-arrivals" },
    {
      label: "About",
      path: "/about-us",
      dropdown: [
        { label: "About Us", path: "/about-us" },
        { label: "Shop", path: "/shop" },
        { label: "Blog", path: "/blog" },
      ],
    },
  ];

  // ── GSAP Timelines ─────────────────────────────────────────────────────
  useEffect(() => {
    let ctx = gsap.context(() => {
      gsap.from(".luxury-header", {
        y: -100,
        opacity: 0,
        duration: 1.2,
        ease: "expo.out",
      });

      searchTl.current = gsap
        .timeline({ paused: true })
        .set(searchOverlayRef.current, { display: "flex" })
        .fromTo(
          ".search-bg",
          { scaleY: 0 },
          { scaleY: 1, duration: 0.8, ease: "expo.inOut" },
        )
        .fromTo(
          [".search-content > *", ".close-search"],
          { y: 30, opacity: 0 },
          { y: 0, opacity: 1, stagger: 0.1, duration: 0.6, ease: "expo.out" },
          "-=0.2",
        );

      menuTl.current = gsap
        .timeline({ paused: true })
        .set(menuOverlayRef.current, { display: "flex" })
        .fromTo(".menu-bg", { opacity: 0 }, { opacity: 1, duration: 0.4 })
        .fromTo(
          ".menu-sidebar",
          { x: "-100%" },
          { x: "0%", duration: 0.8, ease: "expo.inOut" },
          "-=0.3",
        )
        .from(
          ".menu-link",
          { x: -30, opacity: 0, stagger: 0.1, duration: 0.5 },
          "-=0.4",
        );
    });
    return () => ctx.revert();
  }, []);

  useEffect(() => {
    if (searchTl.current) {
      isSearchOpen ? searchTl.current.play() : searchTl.current.reverse();
    }
  }, [isSearchOpen]);

  useEffect(() => {
    if (menuTl.current) {
      isMenuOpen ? menuTl.current.play() : menuTl.current.reverse();
    }
    // Reset accordion on close
    if (!isMenuOpen) setExpandedCat(null);
  }, [isMenuOpen]);

  // ── Debounced Search ───────────────────────────────────────────────────
  useEffect(() => {
    if (!searchText.trim()) {
      setSearchProducts([]);
      return;
    }
    const delay = setTimeout(() => fetchProducts(searchText), 400);
    return () => clearTimeout(delay);
  }, [searchText]);

  const fetchProducts = async (keyword) => {
    setSearchLoading(true);
    try {
      const res = await productAPI.listing({ page: 1, limit: 6, keyword });
      if (res.code === 200) setSearchProducts(res.data.products);
    } catch (err) {
      console.error("Search error:", err);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleCloseSearch = () => {
    setIsSearchOpen(false);
    setTimeout(() => {
      setSearchText("");
      setSearchProducts([]);
    }, 600);
  };

  const handleProductClick = () => handleCloseSearch();

  // ── Toggle mobile accordion ────────────────────────────────────────────
  const toggleMobileCat = (id) =>
    setExpandedCat((prev) => (prev === id ? null : id));

  return (
    <>
      <header className="luxury-header" ref={headerRef}>
        {/* ── TOP UTILITY BAR ── */}
        <div className="lh-utility-bar">
          <div className="lh-icons">
            <Link
              to="#"
              className="icon-link"
              onClick={(e) => {
                e.preventDefault();
                setIsSearchOpen(true);
              }}
            >
              <Search size={20} strokeWidth={1.5} />
            </Link>
            <Link to="/wishlist" className="icon-link d-none d-md-flex">
              <Heart size={20} strokeWidth={1.5} />
            </Link>
            <Link
              to={isLoggedIn ? "/users/dashboard" : "/users/login"}
              className="icon-link d-none d-md-flex"
            >
              <User size={20} strokeWidth={1.5} />
            </Link>
            <Link
              to="#"
              className="icon-link position-relative"
              onClick={(e) => {
                e.preventDefault();
                setIsCartOpen(true);
              }}
            >
              <ShoppingBag size={20} strokeWidth={1.5} />
              {cartCount > 0 && (
                <span className="cart-icon-badge">{cartCount}</span>
              )}
            </Link>
          </div>
        </div>

        {/* ── MAIN HEADER ROW ── */}
        <div className="lh-main-row">
          {/* HAMBURGER — mobile only */}
          <Link
            to="#"
            className="icon-link lh-hamburger d-lg-none m-0"
            onClick={(e) => {
              e.preventDefault();
              setIsMenuOpen(true);
            }}
          >
            <Menu size={24} strokeWidth={1.5} />
          </Link>

          {/* LOGO */}
          <Link to="/" className="brand-logo">
            <img src={config?.logo} alt={config?.name} />
          </Link>

          {/* DESKTOP NAV */}
          <nav className="lh-nav d-none d-lg-flex">
            {/* ── Categories with dropdown ── */}
            {categories && categories.length > 0 ? (
              categories.map((cat) => {
                const hasSubs = cat?.children && cat?.children.length > 0;
                return (
                  <div
                    key={cat._id}
                    className={`nav-item-wrap${hasSubs ? " has-dropdown" : ""}`}
                  >
                    <Link
                      to={`/categories/${cat.slugPath}`}
                      className="nav-link-item"
                    >
                      {cat.name}
                      {hasSubs && (
                        <ChevronDown
                          size={12}
                          strokeWidth={1.5}
                          className="nav-chevron"
                        />
                      )}
                    </Link>

                    {hasSubs && (
                      <div className="nav-dropdown">
                        <div className="nav-dropdown-inner">
                          {cat.children.map((sub) => (
                            <Link
                              key={sub._id}
                              to={`/categories/${sub.slugPath}`}
                              className="nav-dropdown-item"
                            >
                              {sub.name}
                            </Link>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })
            ) : (
              <Link to="/shop" className="nav-link-item">
                Collections
              </Link>
            )}

            {/* ── Static links ── */}
            {staticNavLinks.map((link, i) => (
              <div
                key={i}
                className={`nav-item-wrap${link.dropdown ? " has-dropdown" : ""}`}
              >
                <Link
                  to={link.path}
                  className={`nav-link-item${link.label === "New Arrivals" ? " nav-link-new" : ""}`}
                >
                  {link.label === "New Arrivals" && (
                    <span className="new-badge">NEW</span>
                  )}
                  {link.label}
                  {link.dropdown && (
                    <ChevronDown
                      size={12}
                      strokeWidth={1.5}
                      className="nav-chevron"
                    />
                  )}
                </Link>

                {link.dropdown && (
                  <div className="nav-dropdown">
                    <div className="nav-dropdown-inner">
                      {link.dropdown.map((item, j) => (
                        <Link
                          key={j}
                          to={item.path}
                          className="nav-dropdown-item"
                        >
                          {item.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </nav>

          {/* MOBILE CART */}
          <div className="d-flex">
             <Link
              to="#"
              className="icon-link text-dark"
              onClick={(e) => {
                e.preventDefault();
                setIsSearchOpen(true);
              }}
            >
              <Search size={20} strokeWidth={1.5} />
            </Link>
          <Link
            to="#"
            className=" icon-link position-relative d-lg-none lh-mobile-cart "
            onClick={(e) => {
              e.preventDefault();
              setIsCartOpen(true);
            }}
          >
            <ShoppingBag size={22} strokeWidth={1} />
            {cartCount > 0 && (
              <span className="cart-icon-badge">{cartCount}</span>
            )}
          </Link>
          </div>
         
        </div>
      </header>

      {/* ── OVERLAYS ── */}

      {/* 1. SEARCH */}
      <div className="manual-overlay" ref={searchOverlayRef}>
        <div className="search-bg" />
        <Link
          className="close-btn-luxury close-search"
          style={{ right: "3rem" }}
          onClick={handleCloseSearch}
        >
          <X size={35} />
        </Link>
        <div className="search-content container d-flex flex-column align-items-center justify-content-center h-100 position-relative">
          <h6
            style={{ color: "var(--color-accent-main)", letterSpacing: "4px" }}
          >
            CURATED SEARCH
          </h6>
          <input
            type="text"
            className="luxury-input"
            placeholder="What are you looking for?"
            autoFocus
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
          {searchText.trim() && (
            <div className="search-dropdown">
              {searchLoading && (
                <p className="search-dropdown-status">Searching…</p>
              )}
              {!searchLoading && searchProducts.length === 0 && (
                <p className="search-dropdown-status">
                  No products found for "{searchText}"
                </p>
              )}
              {!searchLoading &&
                searchProducts.map((product) => {
                  const image = product?.thumbnail || product?.images?.[0];
                  const price = product?.variants?.[0]?.price || product?.price;
                  return (
                    <Link
                      key={product._id}
                      to={`/shop/${product.slug}`}
                      className="search-item text-decoration-none"
                      onClick={handleProductClick}
                    >
                      <img
                        src={image}
                        className="search-img"
                        alt={product.title}
                      />
                      <div>
                        <p className="search-title">{product.title}</p>
                        <p className="search-price">
                          ₹{price?.toLocaleString("en-IN")}
                        </p>
                      </div>
                    </Link>
                  );
                })}
              {!searchLoading && searchProducts.length > 0 && (
                <Link
                  to={`/shop?search=${searchText}`}
                  className="search-view-all"
                  onClick={handleProductClick}
                >
                  View all results →
                </Link>
              )}
            </div>
          )}
        </div>
      </div>

      {/* 2. CART */}
      <CartOffCanvas isOpen={isCartOpen} onClose={() => setIsCartOpen(false)} />

      {/* 3. MOBILE MENU */}
      <div className="manual-overlay" ref={menuOverlayRef}>
        <div
          className="menu-bg"
          style={{
            position: "absolute",
            inset: 0,
            background: "rgba(0,0,0,0.5)",
          }}
        />
        <div className="sidebar menu-sidebar d-flex flex-column">
          <button
            className="close-btn-luxury"
            style={{ right: "2rem" }}
            onClick={() => setIsMenuOpen(false)}
          >
            <X size={28} />
          </button>

          <span className="brand-logo mb-4" style={{ fontSize: "1.5rem" }}>
            <img src={config?.logo} alt={config?.title} />
          </span>

          <div className="mobile-nav-list">
            {/* ── Mobile Categories with accordion ── */}
            {categories &&
              categories.length > 0 &&
              categories.map((cat) => {
                const hasSubs = cat.children && cat.children.length > 0;
                const isOpen = expandedCat === cat._id;

                return (
                  <div key={cat._id} className="mobile-cat-group menu-link">
                    <div className="mobile-cat-header">
                      <Link
                        to={`/categories/${cat.slugPath}`}
                        className="nav-link-item ms-0 fs-4"
                        onClick={() => setIsMenuOpen(false)}
                      >
                        {cat.name}
                      </Link>
                      {hasSubs && (
                        <button
                          className="mobile-cat-toggle"
                          onClick={() => toggleMobileCat(cat._id)}
                          aria-label={isOpen ? "Collapse" : "Expand"}
                        >
                          <ChevronRight
                            size={24}
                            strokeWidth={2}
                            className={`toggle-icon${isOpen ? " rotated" : ""}`}
                          />
                        </button>
                      )}
                    </div>

                    {hasSubs && (
                      <div
                        className={`mobile-sub-list${isOpen ? " open" : ""}`}
                      >
                        {cat?.children.map((sub) => (
                          <Link
                            key={sub._id}
                            to={`/categories/${sub.slugPath}`}
                            className="mobile-sub-item"
                            onClick={() => setIsMenuOpen(false)}
                          >
                            {sub.name}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}

            {/* ── Static links ── */}
            {staticNavLinks.map((link, i) => {
              const hasSubs = link.dropdown && link.dropdown.length > 0;
              const isOpen = expandedCat === `static-${i}`;

              return hasSubs ? (
                <div key={i} className="mobile-cat-group menu-link">
                  <div className="mobile-cat-header">
                    <Link
                      to={link.path}
                      className="nav-link-item ms-0 fs-4"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      {link.label}
                    </Link>
                    <button
                      className="mobile-cat-toggle"
                      onClick={() =>
                        setExpandedCat(isOpen ? null : `static-${i}`)
                      }
                      aria-label={isOpen ? "Collapse" : "Expand"}
                    >
                      <ChevronRight
                        size={24}
                        strokeWidth={2}
                        className={`toggle-icon${isOpen ? " rotated" : ""}`}
                      />
                    </button>
                  </div>
                  <div className={`mobile-sub-list${isOpen ? " open" : ""}`}>
                    {link.dropdown.map((item, j) => (
                      <Link
                        key={j}
                        to={item.path}
                        className="mobile-sub-item"
                        onClick={() => setIsMenuOpen(false)}
                      >
                        {item.label}
                      </Link>
                    ))}
                  </div>
                </div>
              ) : (
                <Link
                  key={i}
                  to={link.path}
                  className="menu-link nav-link-item ms-0 fs-4"
                  onClick={() => setIsMenuOpen(false)}
                >
                  {link.label}
                </Link>
              );
            })}

            <Link
              to={isLoggedIn ? "/users/dashboard" : "/users/login"}
              className="menu-link nav-link-item ms-0 fs-4"
              onClick={() => setIsMenuOpen(false)}
            >
              {isLoggedIn ? "My Account" : "Login"}
            </Link>
          </div>
        </div>
      </div>
    </>
  );
};

export default Header;
